import ErrorBoundary from "@/components/ErrorBoundary/errorBoundary.component";
import Navbar from "@/infrastructure/Navbar/navbar.component";
import Sidebar from "@/infrastructure/Sidebar/sidebar.component";
import { AppContext } from "@/services/AppContext/appContext";
import { Toaster } from "@/shadcn/components/ui/sonner";
import { checkPageAccess } from "@/utils/rolePermissions.util";
import React, {
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
export type BaseCurrentPage =
  | "notFound"
  | "dashboard"
  | "leads"
  | "quotation"
  | "reportsList"
  | "orderTracking"
  | "item"
  | "userManagement"
  | "adminManagement"
  | "profile"
  | "mergeCompanies";
type BasePageProps = {
  children: React.ReactNode;
  currPage: BaseCurrentPage;
};

const BasePage: React.FC<BasePageProps> = ({ children, currPage }) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const { userShouldRegisterAccount, userRole } = useContext(AppContext);
  const [isPageAccessible, setIsPageAccessible] = useState<
    boolean | "checking"
  >("checking");

  const handleClickOutside = (event: MouseEvent) => {
    if (
      sidebarRef.current &&
      !sidebarRef.current.contains(event.target as Node)
    ) {
      setIsSidebarOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useLayoutEffect(() => {
    if (!userRole || !currPage) {
      console.error(
        "BasePage: userRole or currPage is not defined. Ensure AppContext is properly initialized."
      );
      return;
    }

    setIsPageAccessible(checkPageAccess(userRole, currPage));
  }, [userRole, currPage]);

  return (
    <div className="flex h-full flex-col">
      <ErrorBoundary>
        <Navbar
          currPage={currPage}
          isSidebarOpen={isSidebarOpen}
          setIsSidebarOpen={setIsSidebarOpen}
        />
      </ErrorBoundary>
      <div className="relative flex h-full flex-1 overflow-hidden">
        <ErrorBoundary>
          <div ref={sidebarRef} className="h-full">
            <Sidebar
              currPage={currPage}
              isSidebarOpen={isSidebarOpen}
              setIsSidebarOpen={setIsSidebarOpen}
            />
          </div>
        </ErrorBoundary>
        <div className="h-full w-full">
          <ErrorBoundary>
            {userShouldRegisterAccount &&
            currPage !== "profile" ? null : isPageAccessible ===
              "checking" ? null : !isPageAccessible ? (
              <div className="mt-4 text-center">
                <h1 className="text-2xl font-bold">Access denied</h1>
                <p>
                  You do not have permission to access this page. Please contact
                  your administrator if you believe this is an error.
                </p>
              </div>
            ) : (
              children
            )}
          </ErrorBoundary>
        </div>
        <Toaster
          position="top-right"
          richColors
          closeButton
          offset={{
            top: "4rem",
          }}
          mobileOffset={{ top: "5rem", left: "10%" }}
          visibleToasts={1}
          expand={true}
        />
      </div>
    </div>
  );
};

export default BasePage;
